import React, {createRef} from 'react';

import { ApplicationState, ConnectedReduxProps } from '../../../../store';
import Dropzone, { DropEvent, FileRejection } from 'react-dropzone';
import { addOtherAttachments, deleteOtherAttachments, setOtherAttachmentsRequestState, updateDescription, uploadOtherAttachments } from '../../../../store/details/other-attachments/actions';

import { CommunicationOverviewDocumentItem, DocumentTypeTC } from '../../../../store/details/overview/types';
import { Dispatch } from 'redux';
import { createOtherAttachment, OtherAttachment } from '../../../../store/details/other-attachments/model';
import { OtherAttachmentItem } from './other-attachment-item';
import { OtherAttachmentsUIState } from '../../../../store/details/other-attachments/types';
import { connect } from 'react-redux';
import { getDownloadLink } from '../../../../store/shared/actions';
import styles from './other-attachments.module.css';

type FileWithPreview = File & {
  preview: string;
};

interface PropsFromState {
  readonly currentClaimNotificationId: number;
  readonly otherAttachments: OtherAttachment[];
  readonly requestState: OtherAttachmentsUIState;
  readonly urlForDownload: string;
  readonly urlForPreview: string;
  readonly uiDisabled: boolean;
  readonly sentDocuments: CommunicationOverviewDocumentItem[];
  readonly caseBlocked: boolean;
  readonly caseCode: string;
  readonly instructionId: number;
}

interface PropsFromDispatch {
  readonly uploadOtherAttachments: typeof uploadOtherAttachments;
  readonly deleteOtherAttachments: typeof deleteOtherAttachments;
  readonly setOtherAttachmentsRequestState: typeof setOtherAttachmentsRequestState;
  readonly addOtherAttachments: typeof addOtherAttachments;
  readonly updateDescription: typeof updateDescription;
  readonly getDownloadLink: typeof getDownloadLink;
}

interface Props {
  readonly handleDownloadClick: (amazonKey: string, filename: string) => void; // Handle independently
}

interface State {
  readonly otherAttachments: OtherAttachment[];
  readonly requestState: OtherAttachmentsUIState;
}

type AllProps = PropsFromState & PropsFromDispatch & ConnectedReduxProps & Props;

class OtherAttachments extends React.Component<AllProps, State> {
  private dropzoneRef: any = createRef();

  isDisabled = (item: OtherAttachment) => {
    if (this.props.caseBlocked) { return true; }
    if (this.props.sentDocuments.some(doc => doc.id === item.id)) { return true; }
    return false;
  }

  openDialog = () => {
    if (this.dropzoneRef.current) {
      this.dropzoneRef.current.open();
    }
  };

  get otherAttachments() {
    return this.props.otherAttachments.map((attachment, i) => (
      <OtherAttachmentItem
        key={i}
        disabled={this.isDisabled(attachment)}
        filename={attachment.filename}
        url={attachment.url}
        amazonKey={attachment.amazonKey}
        content={attachment.content}
        amazonId={attachment.amazonId}
        description={attachment.description}
        isOnline={attachment.isOnline}
        onDescriptionChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
          this.onDescriptionChange(event.target.value, i)
        }
        onFileRemoveClick={() => this.onDeleteItem(i)}
        onUploadClick={() => this.onUploadItem(i)}
        handleDownloadClick={this.props.handleDownloadClick} // Handled directly in the UI layer
        VirusDetected={attachment.VirusDetected}
        AVScanned={attachment.AVScanned}
      />
    ));
  }

  async onFilesDropped(files: FileWithPreview[]) {
    if (this.props.caseBlocked) { return; }
    const otherAttachmentsPromises = files.map(x => {
      return new Promise<OtherAttachment>((resolve) => {
        
        if ((x.name.indexOf('.docx') > -1 ||
          x.name.indexOf('.doc') > -1 ||
          x.name.indexOf('.pdf') > -1 ||
          x.name.indexOf('.xls') > -1 ||
          x.name.indexOf('.xlsx') > -1 ||
          x.name.indexOf('.jpeg') > -1 ||
          x.name.indexOf('.jpg') > -1 ||
          x.name.indexOf('.txt') > -1) &&
          x.type !== 'application/x-msdownload') {
            const oa = createOtherAttachment();
            oa.filename = x.name;
            oa.url = x.preview || '';
            oa.description = '';
            oa.documentType = DocumentTypeTC.otherAttachment;
            oa.isOnline = false;
            const fileReader = new FileReader();
            fileReader.onloadend = () => {
              let c = '';
              if (typeof fileReader.result === 'string') {
                c = fileReader.result.split(',')[1] || '';
              } else if (fileReader.result instanceof ArrayBuffer) {
                const uint8Array = new Uint8Array(fileReader.result);
                const binaryString = String.fromCharCode(...uint8Array);
                c = binaryString.split(',')[1] || '';
              }
              const fc = atob(c);
              const fcf = fc.charAt(0) + fc.charAt(1);
              if (fcf !== 'MZ') {
                oa.content = c;
                resolve(oa);
                return;
              } else {
                return;
              }
            };
            fileReader.readAsDataURL(x);
            return;
        } else {
          return;
        }
      });
    });

    const otherAttachments = await Promise.all(otherAttachmentsPromises);

    this.props.addOtherAttachments(otherAttachments);
  }

  render() {
    const handleDrop = (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
      const filesWithPreview = acceptedFiles.map(file =>
        Object.assign(file, { preview: URL.createObjectURL(file) })
      ) as FileWithPreview[];
      this.onFilesDropped(filesWithPreview);
    };

    return (
      <div style={{ height: '100%' }}>
        <div className={styles.container}>
          <div className={styles.otherAttachments}>
            <Dropzone
              ref={this.dropzoneRef}
              noKeyboard={true}
              onDrop={handleDrop}
              noClick={true}
            >
              {({ getRootProps, getInputProps }) => (
                <div {...getRootProps()} className={styles.dropzone}>
                  <input {...getInputProps()} />
                  {this.otherAttachments}
                </div>
              )}
            </Dropzone>
          </div>
          <div className={styles.actionsColumn}>
            <div
              className={styles.add}
              onClick={() => {
                this.openDialog();
              }}
            >
              <img
                className={styles.img}
                src={require('../../../../assets/images/add_85x85.png')}
                alt=""
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  private onDeleteItem(index: number) {
    if (!this.props.caseBlocked) {
      this.props.deleteOtherAttachments(this.props.caseCode, index);
    }
  }

  private onUploadItem(index: number) {
    if (!this.props.caseBlocked) {
      this.props.uploadOtherAttachments(this.props.instructionId, index);
    }
  }

  private onDescriptionChange(value: string, index: number) {
    if (!this.props.caseBlocked) {
      this.props.updateDescription(index, value);
    }
  }
}
const mapStateToProps = ({ caseState, details: { detailsOtherAttachments, detailsOverview }, shared }: ApplicationState): PropsFromState => ({
  currentClaimNotificationId: caseState.selectedCase.claimNotificationId,
  requestState: detailsOtherAttachments.requestState,
  otherAttachments: detailsOtherAttachments.otherAttachments,
  urlForDownload: detailsOtherAttachments.urlForDownload,
  urlForPreview: detailsOtherAttachments.urlForPreview,
  uiDisabled: shared.isUIDisabled,
  sentDocuments: detailsOverview.documentList,
  caseBlocked: caseState.selectedCase.blocked,
  caseCode: caseState.selectedCase.caseCode,
  instructionId: caseState.selectedCase.instructionId,
});

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
  addOtherAttachments: (attachments: OtherAttachment[]) => dispatch(addOtherAttachments(attachments)),
  uploadOtherAttachments: (instructionId: number, index: number) => dispatch(uploadOtherAttachments(instructionId, index)),
  deleteOtherAttachments: (caseCode: string, index: number) => dispatch(deleteOtherAttachments(caseCode, index)),
  updateDescription: (index: number, description: string) =>
    dispatch(updateDescription(index, description)),
  setOtherAttachmentsRequestState: (requestState: OtherAttachmentsUIState) =>
    dispatch(setOtherAttachmentsRequestState(requestState)),
  getDownloadLink: (amazonKey: string, url: string, content: string, filename: string, onClickLink) => dispatch(getDownloadLink(amazonKey, url, content, filename, onClickLink))
});

export default connect(mapStateToProps, mapDispatchToProps)(OtherAttachments);
